import { I18n } from '@calendly/i18n'
import { textColorL3 } from '@calendly/ui/theme'
import { groupBy, isEmpty, camelCase } from 'lodash-es'
import moment from 'moment-timezone'

import { convertHostIdsToMeetingRoles } from 'components/meetings'
import {
  ANALYTICS_MAP,
  allValue,
  allowedScopeTypes,
  nonHostValue,
  Periods,
  SchedulingMethods,
  Statuses
} from 'constants/meetings'
import { getVideoLocationKinds, getVideoLocationTypes } from 'features/meeting_intelligence'
import { camelize } from 'services/string_utils'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'


// Re-export from canonical source (used by features/meetings/index.js)
export {
  isPendingEvent,
  transformLocationKind,
} from 'components/meetings'

export { mapScopeTypeToBackendValue }

export const buildSearchFilters = (
  scopeType,
  scopeId,
  filterParams,
  isOrganizer,
  includeNotetakerIneligibleExternalEvents,
) => {
  if (!filterParams) return null

  const validFilterProperties = new Set([
    'period',
    'past',
    'startDate',
    'endDate',
    'inviteeEmails',
    'eventTypeIds',
    'schedulingMethods',
    'statusIds',
    'uuid',
    'userIds',
    'teamIds',
    'isOrganizer',
    'includeNotetakerIneligibleExternalEvents',
    'utmCampaign',
    'utmContent',
    'utmMedium',
    'utmSource',
    'utmTerm',
    'scopeType',
    'scopeId',
    'searchKeyword',
    'meetingIntelligenceStatus'
  ])

  const camelizedParams = camelize({
    ...filterParams,
    scopeType,
    scopeId,
    isOrganizer,
    includeNotetakerIneligibleExternalEvents,
  })

  const filters = {}
  const roles = convertHostIdsToMeetingRoles(filterParams.host_ids, scopeType, scopeId)
  if (roles) {
    filters.role = roles
  }

  if (scopeType === 'users' && !scopeId) {
    filters.ownerType = 'USER'
  } else if (scopeType === 'teams' && !scopeId) {
    filters.ownerType = 'TEAM'
  }

  if (filterParams.start_date && filterParams.end_date) {
    filters.startDate = filterParams.start_date
    filters.endDate = filterParams.end_date
  }

  if (isOrganizer !== undefined) {
    filters.isOrganizer = isOrganizer
  }

  for (const [key, value] of Object.entries(camelizedParams)) {
    if (validFilterProperties.has(key) && value != null && (!Array.isArray(value) || value.length > 0)) {
      filters[key] = value
    }
  }

  if (filterParams.search_keyword) {
    filters.searchKeyword = filterParams.search_keyword
  }

  if (filters?.scopeType) {
    filters.scopeType = mapScopeTypeToBackendValue(filters.scopeType, filters?.scopeId)

    if (!allowedScopeTypes.includes(filters.scopeType)) {
      filters.scopeType = ''
    }

    if (filters.scopeType === 'my_calendly') {
      filters.scopeId = ''
    }
  }

  if (!(scopeType === 'user' && scopeId === 'me')) {
    filters.schedulingMethods = [SchedulingMethods.CALENDLY]
  }

  return filters
}

export const isSupportedLocation = (event, hasMsTeamsFlag = false) => {
  const eligibleLocationKinds = getVideoLocationKinds(hasMsTeamsFlag)
  const eligibleLocationTypes = getVideoLocationTypes(hasMsTeamsFlag)
  return eligibleLocationKinds.includes(event?.locationKind)
    || eligibleLocationTypes.includes(event?.locationType)
    || eligibleLocationKinds.includes(event?.location_kind)
}

export const mapNodeToMeeting = (node, publisherTZ, hasMsTeamsFlag = false, currentTime = moment()) => {
  const attendees = (node.attendees || []).map((attendee) => ({ ...attendee, policy: { noShow: false, update: false } }))
  const groupedAttendees = groupBy(attendees, 'kind')
  const allInvitees = groupedAttendees.INVITEE || []
  const invitees = allInvitees.filter((inv) => !inv.cancelled)
  const hosts = groupedAttendees.HOST || []
  const cohosts = groupedAttendees.COHOST || []
  const externalAttendees = groupedAttendees.EXTERNAL || []

  const updated = {
    ...camelize(node),
    startTime: moment(node.start_time || node.startTime).tz(publisherTZ).format(),
    endTime: moment(node.end_time || node.endTime).tz(publisherTZ).format(),
    source: node.kind,
    eventType: {
      ...camelize(node.event_type || {}),
      ...(node.kind !== 'CALENDLY' ? { color: textColorL3 } : {}),
      kind: allInvitees.length > 1 ? 'group' : 'solo', // TODO: we need to read this kind from the node, but it doesn't exist yet
    },
    policy: {
      update: false,
      addAiNotetaker: isSupportedLocation(node, hasMsTeamsFlag) && !node.cancelled
    },
    invitee: invitees[0] || {},
    filledCount: invitees.length,
    eventMemberships: hosts
      .concat(cohosts)
      .concat(externalAttendees)
      .filter(Boolean),
    invitees,
    attendees,
    host: hosts[0],
    past: moment(node.end_time || node.endTime).isBefore(currentTime),
  }
  return updated
}

export const mapChangedFiltersForAnalytics = (changedFilters) => {
  if (isEmpty(changedFilters)) return []
  if (!ANALYTICS_MAP) return []

  return changedFilters
    .map(camelCase)
    .filter((filter) => filter in ANALYTICS_MAP)
    .map((filter) => ANALYTICS_MAP[filter])
}

const UTM_PARAMS = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term']

export const isFilterSet = (filterParams) => {
  if (filterParams.period !== Periods.UPCOMING) return true

  // Check if status is different from default (just 'active')
  const isDefaultStatus = filterParams.status_ids?.length === 1 && filterParams.status_ids[0] === Statuses.ACTIVE
  if (!isDefaultStatus) return true

  if (filterParams.event_type_ids?.length > 0) return true
  if (filterParams.host_ids?.includes(allValue) || filterParams.host_ids?.includes(nonHostValue)) return true
  if (filterParams.invitee_emails?.length > 0) return true
  if (filterParams.team_ids?.length > 0) return true
  if (filterParams.user_ids?.length > 0) return true

  // Check UTM/Tracking ID parameters
  if (UTM_PARAMS.some((param) => filterParams[param]?.length > 0)) return true

  if (filterParams.search_keyword) return true
  if (filterParams.meeting_intelligence_status === 'COMPLETED') return true
  return false
}

export const EXTERNAL_SOURCES = {
  CALENDLY: 'CALENDLY',
  GOOGLE: 'GOOGLE',
  OUTLOOK: 'OUTLOOK'
}

/** True for connected-calendar sources (Google, Outlook); host is determined by email. */
export const isExternalCalendarSource = (source) => source === EXTERNAL_SOURCES.GOOGLE || source === EXTERNAL_SOURCES.OUTLOOK

export const getParticipantText = ({
  isHostMeeting,
  source,
  invitees,
  host,
  guestEmails,
}) => {
  const isExternalMeeting = isExternalCalendarSource(source)

  // If user is not the host, show host info
  if (!isHostMeeting) {
    // Don't show "Hosted by" for external meetings
    if (isExternalMeeting) {
      return null
    }
    // For internal meetings when host name is null, use host email
    if (!host?.name) {
      return host?.email ? I18n.t('publisher.meetings.hosted_by', { name: host.email }) : null
    }
    // For internal meetings, use host name
    return I18n.t('publisher.meetings.hosted_by', { name: host.name })
  }

  // If user is the host, show invitee info
  const firstInvitee = invitees?.[0]
  if (!firstInvitee) return null

  // For external meetings or when invitee name is null, use invitee email
  const inviteeName = isExternalMeeting || !firstInvitee.name
    ? firstInvitee.email
    : firstInvitee.name

  if (!inviteeName) return null

  // For multiple invitees, show first invitee and count
  if (invitees?.length > 1) {
    const additionalCount = invitees.length - 1
    return I18n.t('publisher.meetings.link.with_and_guests', {
      name: inviteeName,
      count: additionalCount,
      guest: additionalCount === 1 ? 'guest' : 'guests'
    })
  }

  // For single invitee with guest emails, show invitee and guest count
  if (guestEmails?.length > 0) {
    return I18n.t('publisher.meetings.link.with_and_guests', {
      name: inviteeName,
      count: guestEmails.length,
      guest: guestEmails.length === 1 ? 'guest' : 'guests'
    })
  }

  // For single invitee, just show their name/email
  return I18n.t('publisher.meetings.link.with', { name: inviteeName })
}
